import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ArrowLeft } from "lucide-react";

const CATEGORY_OPTIONS = [
  { value: "permiso_sanitario", label: "Permiso sanitario", preset: { renewal_frequency: "anual", issuer: "" } },
  { value: "certificacion", label: "Certificacion", preset: { renewal_frequency: "anual" } },
  { value: "contrato_alquiler", label: "Contrato de alquiler", preset: { renewal_frequency: "anual" } },
  { value: "contrato_empleado", label: "Contrato empleado", preset: { renewal_frequency: "bianual" } },
  { value: "contrato_proveedor", label: "Contrato proveedor", preset: { renewal_frequency: "anual" } },
  { value: "producto_camara", label: "Producto en camara", preset: { renewal_frequency: "custom" } },
  // Fix WH63 (2026-09-28): registro de marca (IMPI). Es de la empresa titular,
  // no de una sucursal.
  { value: "marca", label: "Marca", preset: { renewal_frequency: "decenal", issuer: "IMPI", branch_id: "" } },
  // Fix WH78 (2026-10-15): e.firma, sellos y accesos a portales (SAT, IMSS,
  // Infonavit) que vencen. Suelen ser de la empresa, no de un local.
  { value: "accesos_contables", label: "Accesos contables", preset: { renewal_frequency: "anual" } },
  { value: "custom", label: "Personalizado", preset: {} },
];

// Fix WH78 (2026-10-15): roles que pueden cargar la misma vigencia para varias
// empresas y sucursales en un paso (los mismos que el endpoint /bulk).
const ROLES_VARIAS = ["corporate_admin", "treasury", "legal"];

export default function OpsVigenciaForm() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Fix WH63 (2026-09-28): el mismo formulario edita una vigencia existente
  // (/ops/vigencias/:id/editar). Antes solo se podían editar las alertas y una
  // fecha mal cargada no tenía arreglo (video de Cintia, Legal).
  const { id } = useParams();
  const editando = Boolean(id);
  const volver = editando ? `/ops/vigencias/${id}` : "/ops/vigencias";
  const [branches, setBranches] = useState([]);
  // Fix WH35 (2026-09-08): razones sociales, para las vigencias que son de la
  // empresa y no de un local (sello digital, RFC, alta patronal).
  const [entities, setEntities] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    branch_id: "",
    legal_entity_id: "",
    category: "",
    name: "",
    description: "",
    issuer: "",
    issue_date: "",
    due_date: "",
    use_declaration_date: "",  // Fix WH63 (2026-09-28): marcas
    renewal_frequency: "anual",
    responsible_user_id: "",
    notify_user_ids: [],
    alert_anticipation_days: "60, 30, 7",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Fix WH78 (2026-10-15): alta para varias empresas y/o sucursales a la vez.
  // Solo al crear; al editar se sigue trabajando con una sola.
  const puedeVarias = !id && ROLES_VARIAS.includes(user?.role);
  const [varias, setVarias] = useState(false);
  const [variasSucursales, setVariasSucursales] = useState([]);
  const [variasEmpresas, setVariasEmpresas] = useState([]);
  const alternar = (lista, setLista, valor) =>
    setLista(lista.includes(valor) ? lista.filter(v => v !== valor) : [...lista, valor]);

  useEffect(() => {
    api.get("/branches").then(r => {
      setBranches(r.data || []);
      // Fix WH63 (2026-09-28): al editar no se pisa la sucursal guardada.
      if (!id && r.data?.length > 0) setForm(f => ({ ...f, branch_id: r.data[0].id }));
    });
    // Fix WH35 (2026-09-08): si no hay ninguna cargada, el selector queda vacio
    // y se sigue trabajando por sucursal como hasta ahora.
    api.get("/ops/legal-entities").then(r => setEntities(r.data || [])).catch(() => {});
    api.get("/ops/users/lite").then(r => setUsers(r.data || [])).catch(() => {});
  }, [id]);

  // Fix WH63 (2026-09-28): en modo edición se cargan los datos guardados (no
  // hay GET de una sola vigencia; el detalle hace lo mismo con la lista).
  useEffect(() => {
    if (!id) return;
    api.get("/ops/vigencias").then(r => {
      const v = (r.data || []).find(x => x.id === id);
      if (!v) { setError("Vigencia no encontrada."); return; }
      setForm({
        branch_id: v.branch_id || "",
        legal_entity_id: v.legal_entity_id || "",
        category: v.category || "custom",
        name: v.name || "",
        description: v.description || "",
        issuer: v.issuer || "",
        issue_date: v.issue_date || "",
        due_date: v.due_date || "",
        use_declaration_date: v.use_declaration_date || "",
        renewal_frequency: v.renewal_frequency || "anual",
        responsible_user_id: v.responsible_user_id || "",
        notify_user_ids: v.notify_user_ids || [],
        alert_anticipation_days: (v.alert_anticipation_days || []).join(", "),
      });
    }).catch(() => setError("No se pudo cargar la vigencia."));
  }, [id]);

  const handleCategoryChange = (cat) => {
    const preset = CATEGORY_OPTIONS.find(c => c.value === cat)?.preset || {};
    setForm(f => ({ ...f, category: cat, ...preset }));
  };

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const toggleNotifyUser = (userId) => {
    setForm(f => ({
      ...f,
      notify_user_ids: f.notify_user_ids.includes(userId)
        ? f.notify_user_ids.filter(id => id !== userId)
        : [...f.notify_user_ids, userId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Fix WH78 (2026-10-15): alta para varias empresas y/o sucursales.
    if (varias) {
      // Una marca es de la empresa titular: las sucursales no cuentan.
      const sucursales = form.category === "marca" ? [] : variasSucursales;
      if (sucursales.length + variasEmpresas.length === 0) {
        setError(form.category === "marca" ? "Marca al menos una empresa titular." : "Marca al menos una empresa o una sucursal.");
        return;
      }
      if (!form.category || !form.name || !form.due_date) {
        setError("Completa los campos obligatorios: categoria, nombre y fecha de vencimiento.");
        return;
      }
      setLoading(true); setError("");
      try {
        const anticipation = form.alert_anticipation_days
          .split(",").map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n) && n >= 0);
        await api.post("/ops/vigencias/bulk", {
          ...form,
          branch_id: null,
          legal_entity_id: null,
          branch_ids: sucursales,
          legal_entity_ids: variasEmpresas,
          responsible_user_id: form.responsible_user_id || null,
          alert_anticipation_days: anticipation.length > 0 ? anticipation : null,
        });
        navigate("/ops/vigencias");
      } catch (e) {
        setError(e?.response?.data?.detail || "Error al crear las vigencias.");
      } finally {
        setLoading(false);
      }
      return;
    }
    // Fix WH35 (2026-09-08): alcanza con UNA de las dos. Un sello digital es de
    // la razon social, no de un local.
    // Fix WH63 (2026-09-28): una marca es de la empresa titular.
    if (form.category === "marca" && !form.legal_entity_id) {
      setError("Para una marca, indica la empresa titular.");
      return;
    }
    if ((!form.branch_id && !form.legal_entity_id) || !form.category || !form.name || !form.due_date) {
      setError("Completa los campos obligatorios: sucursal o empresa, categoria, nombre y fecha de vencimiento.");
      return;
    }
    setLoading(true); setError("");
    try {
      const anticipation = form.alert_anticipation_days
        .split(",").map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n) && n >= 0);
      const payload = {
        ...form,
        alert_anticipation_days: anticipation.length > 0 ? anticipation : null,
      };
      if (editando) {
        // Fix WH63 (2026-09-28): en el PUT un "" en sucursal, empresa,
        // responsable o declaración de uso limpia el campo.
        await api.put(`/ops/vigencias/${id}`, payload);
        navigate(`/ops/vigencias/${id}`);
      } else {
        await api.post("/ops/vigencias", {
          ...payload,
          responsible_user_id: form.responsible_user_id || null,
        });
        navigate("/ops/vigencias");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || (editando ? "Error al guardar los cambios." : "Error al crear la vigencia."));
    } finally {
      setLoading(false);
    }
  };

  const selectedCat = CATEGORY_OPTIONS.find(c => c.value === form.category);

  return (
    <div className="p-6 max-w-xl mx-auto space-y-6" data-testid="ops-vigencia-form-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(volver)} className="p-2 rounded-lg hover:bg-slate-100 transition-colors">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h1 className="text-xl font-bold text-slate-900">{editando ? "Editar vigencia" : "Nueva vigencia"}</h1>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-slate-200 p-6 space-y-5">
        {/* Categoria con presets */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Categoria *</label>
          <div className="grid grid-cols-2 gap-2">
            {CATEGORY_OPTIONS.map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => handleCategoryChange(opt.value)}
                className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors text-left ${
                  form.category === opt.value
                    ? "bg-[#FFDF00] border-yellow-400 text-slate-900"
                    : "border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {selectedCat && (
            <p className="text-xs text-slate-500 mt-1.5">
              {form.category === "permiso_sanitario" && "Ej: 'Permiso sanitario SSA Jalisco' — issuer: SSA Jalisco, due_date: 2027-06-22"}
              {form.category === "contrato_alquiler" && "Ej: 'Renta local Olas Altas' — fecha vencimiento: proximo termino del contrato"}
              {form.category === "certificacion" && "Ej: 'Certificacion HACCP cocina' — renovacion anual"}
              {form.category === "producto_camara" && "Ej: 'Camaron T1 — lote 2026-001' — vence segun fecha de camara"}
              {form.category === "marca" && "Ej: 'Marca CHIZY CHIZ — clase 43' — con fecha de concesion, declaracion de uso y vencimiento"}
              {form.category === "accesos_contables" && "Ej: 'e.firma (FIEL)', 'Sello digital (CSD)' o 'Acceso al portal del IMSS' — de cada empresa"}
            </p>
          )}
        </div>

        {/* Fix WH78 (2026-10-15): cargar la misma vigencia para varias empresas
            y/o sucursales. Se crea una por cada una marcada. */}
        {puedeVarias && (
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer">
            <input type="checkbox" checked={varias} onChange={e => setVarias(e.target.checked)}
              className="w-4 h-4" data-testid="vigencia-varias" />
            Cargar para varias empresas o sucursales a la vez
          </label>
        )}
        {varias ? (
          <div className="space-y-2" data-testid="vigencia-varias-panel">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Empresas (razon social)</label>
                <div className="border border-slate-200 rounded-lg p-2 max-h-48 overflow-y-auto space-y-1">
                  {entities.length === 0 && <p className="text-xs text-slate-400 px-2 py-1">No hay razones sociales cargadas.</p>}
                  {entities.map(e => (
                    <label key={e.id} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50 cursor-pointer text-sm">
                      <input type="checkbox" checked={variasEmpresas.includes(e.id)} className="w-4 h-4"
                        onChange={() => alternar(variasEmpresas, setVariasEmpresas, e.id)} data-testid={`vigencia-varias-empresa-${e.id}`} />
                      <span>{e.legal_name}</span>
                    </label>
                  ))}
                </div>
              </div>
              {form.category !== "marca" && (
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Sucursales</label>
                  <div className="border border-slate-200 rounded-lg p-2 max-h-48 overflow-y-auto space-y-1">
                    {branches.map(b => (
                      <label key={b.id} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50 cursor-pointer text-sm">
                        <input type="checkbox" checked={variasSucursales.includes(b.id)} className="w-4 h-4"
                          onChange={() => alternar(variasSucursales, setVariasSucursales, b.id)} data-testid={`vigencia-varias-sucursal-${b.id}`} />
                        <span>{b.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <p className="text-xs text-slate-500" data-testid="vigencia-varias-resumen">
              Se crea una vigencia por cada empresa y cada sucursal marcada ({variasEmpresas.length + (form.category === "marca" ? 0 : variasSucursales.length)}), con los mismos datos.
              Cada una lleva despues sus documentos, su renovacion y sus alertas por separado.
            </p>
          </div>
        ) : (<>
        {/* Fix WH35 (2026-09-08): sucursal O empresa. Se pueden usar las dos
            (un permiso de un local de una razon social concreta), pero hace
            falta al menos una. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Sucursal</label>
            <select value={form.branch_id} onChange={set("branch_id")} data-testid="vigencia-sucursal"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
              <option value="">— Ninguna (es de la empresa) —</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              {form.category === "marca" ? "Empresa titular de la marca *" : "Empresa (razon social)"}
            </label>
            <select value={form.legal_entity_id} onChange={set("legal_entity_id")} data-testid="vigencia-empresa"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
              <option value="">— Ninguna (es de la sucursal) —</option>
              {entities.map(e => <option key={e.id} value={e.id}>{e.legal_name}</option>)}
            </select>
            {entities.length === 0 && (
              <p className="text-xs text-slate-400 mt-1">
                No hay razones sociales cargadas todavia. Se cargan en Administracion → Razones sociales.
              </p>
            )}
          </div>
        </div>
        <p className="text-xs text-slate-500 -mt-1">
          Indica al menos una. Un sello digital, un RFC o un alta patronal son de la empresa;
          un permiso sanitario o un alquiler, de la sucursal.
        </p>
        </>)}

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Nombre *</label>
          <input type="text" value={form.name} onChange={set("name")}
            placeholder={form.category === "permiso_sanitario" ? "Ej: Permiso sanitario sucursal Olas Altas"
              : form.category === "marca" ? "Ej: Marca CHIZY CHIZ — clase 43" : "Nombre descriptivo"}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" required />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Emisor / Organismo</label>
            <input type="text" value={form.issuer} onChange={set("issuer")}
              placeholder="Ej: SSA Jalisco"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Frecuencia renovacion</label>
            <select value={form.renewal_frequency} onChange={set("renewal_frequency")}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
              {[["anual","Anual"],["bianual","Bianual"],["trianual","Trianual"],["decenal","Cada 10 años"],["indefinido","Indefinido"],["custom","Personalizado"]].map(([v,l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">
              {form.category === "marca" ? "Fecha de concesion" : "Fecha de emision"}
            </label>
            <input type="date" value={form.issue_date} onChange={set("issue_date")}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Fecha de vencimiento *</label>
            <input type="date" value={form.due_date} onChange={set("due_date")}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" required />
          </div>
        </div>

        {/* Fix WH63 (2026-09-28): marcas — tercera fecha, la de la declaracion
            de uso ante el IMPI. Se avisa igual que el vencimiento. */}
        {form.category === "marca" && (
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Fecha para la declaracion de uso</label>
            <input type="date" value={form.use_declaration_date} onChange={set("use_declaration_date")}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="vigencia-uso-input" />
            {/* Fix WH78 (2026-10-15): el aviso llega tambien a Legal. */}
            <p className="text-xs text-slate-400 mt-1">Se avisa con los mismos dias de anticipacion que el vencimiento, a Legal, al responsable y a corporativo.</p>
          </div>
        )}

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Descripcion (opcional)</label>
          <textarea value={form.description} onChange={set("description")} rows={2}
            placeholder="Notas adicionales sobre esta vigencia..."
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none" />
        </div>

        <div className="border-t border-slate-100 pt-4">
          <h3 className="text-sm font-bold text-slate-900 mb-3">Alertas y responsables</h3>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Responsable</label>
            <select value={form.responsible_user_id} onChange={set("responsible_user_id")}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="vigencia-responsable-select">
              <option value="">Sin responsable asignado</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.email} ({u.role})</option>)}
            </select>
            <p className="text-xs text-slate-400 mt-1">Recibe las alertas de vencimiento junto con corporativo.</p>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Destinatarios adicionales</label>
            <div className="border border-slate-200 rounded-lg p-2 max-h-40 overflow-y-auto space-y-1" data-testid="vigencia-destinatarios-list">
              {users.map(u => (
                <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50 cursor-pointer text-sm">
                  <input type="checkbox" checked={form.notify_user_ids.includes(u.id)}
                    onChange={() => toggleNotifyUser(u.id)} className="w-4 h-4" />
                  <span>{u.full_name || u.email} <span className="text-slate-400">({u.role})</span></span>
                </label>
              ))}
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Días de anticipación de alerta</label>
            <input type="text" value={form.alert_anticipation_days} onChange={set("alert_anticipation_days")}
              placeholder="60, 30, 7"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="vigencia-anticipation-input" />
            <p className="text-xs text-slate-400 mt-1">Días antes del vencimiento para disparar alerta (separados por coma). Por defecto: 60, 30, 7.</p>
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <button type="button" onClick={() => navigate(volver)}
            className="flex-1 py-2.5 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors">
            Cancelar
          </button>
          <button type="submit" disabled={loading}
            className="flex-1 py-2.5 bg-[#FFDF00] text-slate-900 rounded-lg font-semibold text-sm hover:bg-yellow-400 transition-colors disabled:opacity-50"
            data-testid="btn-guardar-vigencia">
            {loading ? "Guardando..." : editando ? "Guardar cambios" : varias ? "Guardar vigencias" : "Guardar vigencia"}
          </button>
        </div>
      </form>
    </div>
  );
}
